/**
 * Required product options (3am Latte custom).
 *
 * Options listed in the variant picker block's "Options shoppers must choose
 * themselves" setting (default: Size) start with nothing selected. Until the
 * shopper picks one:
 *   - Add to cart (and the sticky add to cart) is blocked and a short message
 *     is shown under the option.
 *   - The accelerated checkout buttons (Shop Pay etc.) are hidden.
 *
 * Which options are required is marked in Liquid with
 * `fieldset[data-require-selection="<option name>"]`.
 *
 * Why this lives in JS state and not in the DOM: the variant picker re-renders
 * itself from the server after every change, and the server always marks the
 * first available variant as selected. We keep our own record of what the
 * shopper has actually chosen and re-apply it after each re-render
 * (`variant-picker:updated`, dispatched from variant-picker.js).
 */

const FIELDSET_SELECTOR = 'fieldset[data-require-selection]';
const LOCKED_ATTRIBUTE = 'data-selection-required';

/** @type {WeakMap<Element, Set<string>>} */
const chosenByPicker = new WeakMap();

/**
 * Option names the shopper has explicitly chosen, per variant picker.
 * Seeded from the server render: a fieldset marked `is-chosen` means the page
 * was opened with a specific `?variant=`, so that option counts as chosen.
 * @param {Element} picker
 * @returns {Set<string>}
 */
function getChosen(picker) {
  let chosen = chosenByPicker.get(picker);

  if (!chosen) {
    chosen = new Set();
    // `?unchosen=size` (added to links from product cards and colour swatches) opens the page
    // with a pack/variant preselected but the named option left for the shopper to pick.
    const unchosen = (new URLSearchParams(window.location.search).get('unchosen') || '')
      .toLowerCase()
      .split(',')
      .map((name) => name.trim());
    for (const fieldset of picker.querySelectorAll(FIELDSET_SELECTOR)) {
      if (fieldset.classList.contains('is-chosen') && !unchosen.includes(optionName(fieldset).toLowerCase())) {
        chosen.add(optionName(fieldset));
      }
    }
    chosenByPicker.set(picker, chosen);
  }

  return chosen;
}

/** @param {Element} fieldset */
function optionName(fieldset) {
  return /** @type {HTMLElement} */ (fieldset).dataset.requireSelection ?? '';
}

/**
 * Required fieldsets the shopper hasn't chosen yet.
 * @param {Element} picker
 * @returns {HTMLFieldSetElement[]}
 */
function getUnchosen(picker) {
  const chosen = getChosen(picker);
  return /** @type {HTMLFieldSetElement[]} */ (
    Array.from(picker.querySelectorAll(FIELDSET_SELECTOR)).filter((fieldset) => !chosen.has(optionName(fieldset)))
  );
}

/** @param {Element} element */
function getContainer(element) {
  return element.closest('product-card') ?? element.closest('dialog') ?? element.closest('.shopify-section');
}

/**
 * The variant picker that belongs to a product form (same lookup the theme's own product form uses).
 * @param {HTMLElement} component - A product-form-component.
 * @returns {Element | null}
 */
function findPicker(component) {
  const container = getContainer(component);
  if (!container) return null;

  const pickers = Array.from(container.querySelectorAll('variant-picker'));
  const productId = component.dataset.productId;
  const match = productId ? pickers.find((picker) => /** @type {HTMLElement} */ (picker).dataset.productId === productId) : null;

  return match ?? (pickers.length === 1 ? pickers[0] ?? null : null);
}

/** @param {Element} fieldset */
function clearMessage(fieldset) {
  const message = fieldset.querySelector('[data-required-message]');
  if (message) message.textContent = '';
}

/**
 * Show the "please select" message, bring the option into view and put keyboard focus on it.
 * @param {HTMLFieldSetElement} fieldset
 */
function promptForSelection(fieldset) {
  const message = /** @type {HTMLElement | null} */ (fieldset.querySelector('[data-required-message]'));
  if (message) message.textContent = message.dataset.message || 'Please select an option';

  const { top, bottom } = fieldset.getBoundingClientRect();
  if (top < 0 || bottom > window.innerHeight) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @type {HTMLElement | null} */ (fieldset.querySelector('input[type="radio"]'))?.focus({ preventScroll: true });
}

/**
 * Hide accelerated checkout (Shop Pay etc.) while a required option is unchosen.
 * Done with an attribute + CSS, so the theme re-showing the block after a variant
 * change can't un-hide it.
 * @param {Element} picker
 */
function syncAcceleratedCheckout(picker) {
  const block = getContainer(picker)?.querySelector('.accelerated-checkout-block');
  if (!block) return;

  if (getUnchosen(picker).length > 0) {
    block.setAttribute(LOCKED_ATTRIBUTE, 'true');
  } else {
    block.removeAttribute(LOCKED_ATTRIBUTE);
  }
}

/**
 * Make the picker match what the shopper has actually chosen: required options
 * they haven't touched get their radios unchecked.
 * @param {Element} picker
 */
function syncPicker(picker) {
  if (!picker.querySelector(FIELDSET_SELECTOR)) return;

  const chosen = getChosen(picker);

  for (const fieldset of picker.querySelectorAll(FIELDSET_SELECTOR)) {
    const isChosen = chosen.has(optionName(fieldset));
    fieldset.classList.toggle('is-chosen', isChosen);

    if (isChosen) continue;

    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input[type="radio"]'))) {
      if (input.checked) input.checked = false;
      if (input.dataset.currentChecked === 'true') input.dataset.currentChecked = 'false';
    }
  }

  syncAcceleratedCheckout(picker);
}

function syncAllPickers() {
  document.querySelectorAll('variant-picker').forEach(syncPicker);
}

/**
 * Stops an add-to-cart click / submit when a required option is unchosen.
 * Registered on `window` in the capture phase so it runs before the theme's own
 * (document-level) handlers, including the "added" animation on click.
 * @param {Event} event
 * @param {Element | null} source - An element inside the product form.
 */
function blockIfIncomplete(event, source) {
  const component = /** @type {HTMLElement | null} */ (source?.closest('product-form-component') ?? null);
  if (!component) return;

  const picker = findPicker(component);
  if (!picker) return;

  const [missing] = getUnchosen(picker);
  if (!missing) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  promptForSelection(missing);
}

// The shopper picks a required option: remember it.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

  const fieldset = input.closest(FIELDSET_SELECTOR);
  const picker = fieldset?.closest('variant-picker');
  if (!fieldset || !picker) return;

  getChosen(picker).add(optionName(fieldset));
  fieldset.classList.add('is-chosen');
  clearMessage(fieldset);
  syncAcceleratedCheckout(picker);
});

// The picker re-rendered itself from the server: re-apply what the shopper has (not) chosen.
document.addEventListener('variant-picker:updated', syncAllPickers);

window.addEventListener(
  'click',
  (event) => {
    if (event.target instanceof Element) blockIfIncomplete(event, event.target.closest('add-to-cart-component'));
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    if (event.target instanceof HTMLFormElement) blockIfIncomplete(event, event.target);
  },
  true
);

// Pickers added later (quick-add dialogs, section re-renders).
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches('variant-picker')) {
        syncPicker(node);
      } else {
        node.querySelectorAll('variant-picker').forEach(syncPicker);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

syncAllPickers();
